import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/badge";
import { Button } from "@ndd/ui";
import { cn } from "@/lib/utils";
import { formatLocalDate, startOfWeekIso } from "../../../../shared/local-date";
import { deriveRowTitle } from "../../../../shared/form-registry";
import type { FormSnapshotData } from "../../../../shared/form-types";
import { FormStatusPill } from "./form-snapshot-detail";

const CARD =
  "overflow-hidden rounded-[14px] border border-sand-200 bg-card shadow-[0_1px_2px_rgba(40,3,15,0.05)]";
const CARD_HEAD =
  "flex min-h-[48px] items-center justify-between gap-3 border-b border-sand-200 bg-sand-100 px-4 py-2";
const CARD_TITLE =
  "font-sans text-[12px] font-extrabold uppercase tracking-[0.06em] text-foreground";
const ROW =
  "grid w-full items-center gap-3.5 border-b border-sand-200 px-4 py-[11px] text-left last:border-b-0 hover:bg-sand-50";

function typeLabel(snapshot: FormSnapshotData): string {
  return snapshot.formType === "weekly_form" ? "Weekly" : "Monthly";
}

/** The weekly snapshot immediately preceding `index` in the newest-first list. */
function priorWeekly(
  snapshots: FormSnapshotData[],
  index: number,
): FormSnapshotData | null {
  return (
    snapshots.slice(index + 1).find((s) => s.formType === "weekly_form") ?? null
  );
}

function rowTitle(snapshot: FormSnapshotData, prior: FormSnapshotData | null) {
  const derived = deriveRowTitle(snapshot, prior);
  if (derived) return derived;
  return snapshot.formType === "weekly_form"
    ? `Week of ${formatLocalDate(snapshot.periodStartDate, "short")}`
    : "Monthly checkpoint";
}

interface FormSnapshotListProps {
  snapshots: FormSnapshotData[];
  /** Whether the RD's form profile exists — capture is disabled until it does. */
  canCapture: boolean;
  onAdd: () => void;
  onBackfill: () => void;
  onOpen: (formId: string) => void;
}

export function FormSnapshotList({
  snapshots,
  canCapture,
  onAdd,
  onBackfill,
  onOpen,
}: FormSnapshotListProps) {
  const currentWeekStart = startOfWeekIso();
  const hasCurrentWeekly = snapshots.some(
    (s) =>
      s.formType === "weekly_form" && s.periodStartDate === currentWeekStart,
  );

  return (
    <section className={CARD} aria-label="Form snapshots">
      <div className={CARD_HEAD}>
        <h3 className={CARD_TITLE}>Form snapshots</h3>
        <div className="flex items-center gap-3">
          <span className="whitespace-nowrap text-[12px] text-sand-400">
            {snapshots.length} total
            {snapshots.length > 0 && " · newest first"}
          </span>
          {/* Persistent, quiet backfill affordance — disabled (never hidden)
              until the profile exists, per the CTA-hierarchy review. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackfill}
            disabled={!canCapture}
            title={canCapture ? undefined : "Set up the profile first"}
            className="h-8 rounded-lg px-3 text-[13px] font-semibold text-sand-500 hover:bg-sand-200 hover:text-sand-600"
          >
            + Past week
          </Button>
        </div>
      </div>

      {snapshots.length === 0 ? (
        canCapture ? (
          <button
            type="button"
            onClick={onAdd}
            className={cn(
              ROW,
              "grid-cols-[68px_minmax(0,1fr)_auto] bg-interactive-light hover:bg-interactive-light",
            )}
          >
            <span className="whitespace-nowrap text-[13px] text-sand-500">
              {formatLocalDate(currentWeekStart, "short")}
            </span>
            <span className="truncate text-[13.5px] italic text-sand-500">
              No forms logged yet — capture this week to start the history
            </span>
            <span className="whitespace-nowrap font-sans text-[13px] font-semibold text-interactive">
              Log weekly form →
            </span>
          </button>
        ) : (
          <div className="px-4 py-6 text-center text-[13.5px] text-sand-400">
            Set up the profile to enable weekly forms.
          </div>
        )
      ) : (
        <div>
          {!hasCurrentWeekly && (
            <button
              type="button"
              onClick={onAdd}
              className={cn(
                ROW,
                "grid-cols-[68px_minmax(0,1fr)_auto] bg-sand-50",
              )}
            >
              <span className="whitespace-nowrap text-[13px] text-sand-500">
                {formatLocalDate(currentWeekStart, "short")}
              </span>
              <span className="truncate text-[13.5px] italic text-sand-400">
                This week&rsquo;s form hasn&rsquo;t been logged yet
              </span>
              <span className="whitespace-nowrap text-[12.5px] font-semibold text-interactive">
                Log weekly form
              </span>
            </button>
          )}

          {snapshots.map((snapshot, index) => {
            const isWeekly = snapshot.formType === "weekly_form";
            const title = rowTitle(snapshot, priorWeekly(snapshots, index));
            return (
              <button
                key={snapshot.id}
                type="button"
                onClick={() => onOpen(snapshot.id)}
                aria-label={`Open ${title}`}
                className={cn(ROW, "grid-cols-[68px_78px_148px_minmax(0,1fr)_auto]")}
              >
                <span className="whitespace-nowrap text-[13px] text-sand-500">
                  {formatLocalDate(snapshot.periodStartDate, "short")}
                </span>
                <span>
                  <Badge variant="category">{typeLabel(snapshot)}</Badge>
                </span>
                <span>
                  {isWeekly && <FormStatusPill value={snapshot.status} />}
                </span>
                <span className="truncate text-[13.5px] font-medium text-foreground">
                  {title}
                </span>
                <span className="inline-flex text-sand-300">
                  <ChevronRight className="h-[15px] w-[15px]" />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
